import { useEffect, useMemo, useState } from 'react';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';

const initialState = {
  codigo: '',
  nombre: '',
  descripcion: '',
  limite_kilos: '',
  limite_bultos: '',
  limite_valor_declarado: '',
  limite_m3: '',
  limite_rc: '',
  activa: true,
};

const LIMIT_FIELDS = [
  ['limite_kilos', 'KG', 'Peso máximo'],
  ['limite_bultos', 'Bultos', 'Cantidad máxima'],
  ['limite_valor_declarado', 'VD', 'Valor declarado'],
  ['limite_m3', 'M3', 'Volumen máximo'],
  ['limite_rc', 'RC', 'Contra reembolso'],
];

function toNullableNumber(value, integer = false) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = integer ? Number.parseInt(value, 10) : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function normalizeCodigo(value) {
  return String(value ?? '').trim().toUpperCase().padStart(8, '0');
}

function LimitFieldCard({ label, helper, value, onChange }) {
  return (
    <label className="rounded-2xl border border-[rgba(var(--border),0.52)] bg-[rgba(var(--subpanel),0.72)] p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.9)]">{label}</div>
      <input
        type="number"
        step="any"
        value={value}
        onChange={onChange}
        className="mt-2 h-10 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.94)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]"
      />
      <div className="mt-1 text-[11px] text-[rgba(var(--muted),0.88)]">{helper}</div>
    </label>
  );
}

export default function LineaForm({
  initialValues,
  onSubmit,
  onCancel,
  submitText = 'Guardar',
}) {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});

  const normalizedInitial = useMemo(() => ({ ...initialState, ...initialValues }), [initialValues]);

  useEffect(() => {
    setValues({
      ...normalizedInitial,
      limite_kilos: normalizedInitial.limite_kilos ?? '',
      limite_bultos: normalizedInitial.limite_bultos ?? '',
      limite_valor_declarado: normalizedInitial.limite_valor_declarado ?? '',
      limite_m3: normalizedInitial.limite_m3 ?? '',
      limite_rc: normalizedInitial.limite_rc ?? '',
    });
  }, [normalizedInitial]);

  const handleChange = key => event => {
    const next = key === 'activa' ? event.target.checked : event.target.value;
    setValues(prev => ({ ...prev, [key]: key === 'codigo' ? String(next).toUpperCase() : next }));
  };

  const handleSubmit = event => {
    event.preventDefault();

    const nextErrors = {};
    const normalizedCodigo = normalizeCodigo(values.codigo);

    if (!values.codigo?.trim()) {
      nextErrors.codigo = 'El código es obligatorio.';
    } else if (normalizedCodigo.length > 8) {
      nextErrors.codigo = 'El código no puede tener más de 8 caracteres.';
    }

    if (!values.nombre?.trim()) {
      nextErrors.nombre = 'El nombre es obligatorio.';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit({
      codigo: normalizedCodigo,
      nombre: values.nombre.trim(),
      descripcion: values.descripcion?.trim() || null,
      limite_kilos: toNullableNumber(values.limite_kilos),
      limite_bultos: toNullableNumber(values.limite_bultos, true),
      limite_valor_declarado: toNullableNumber(values.limite_valor_declarado),
      limite_m3: toNullableNumber(values.limite_m3),
      limite_rc: toNullableNumber(values.limite_rc),
      activa: Boolean(values.activa),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-col gap-3">
      <section className="rounded-2xl border border-[rgba(var(--border),0.58)] bg-[rgba(var(--panel),0.96)] p-3 shadow-[0_10px_26px_rgba(var(--shadow),0.08)] md:p-4">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-3">
            <Field
              label="Código"
              htmlFor="linea-codigo"
              hint="Si tiene menos de 8 caracteres, se completa con ceros a la izquierda."
              error={errors.codigo}
              required
            >
              <input
                id="linea-codigo"
                value={values.codigo}
                onChange={handleChange('codigo')}
                maxLength={8}
                className="h-10 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.94)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>

          <div className="col-span-12 md:col-span-7">
            <Field label="Nombre" htmlFor="linea-nombre" error={errors.nombre} required>
              <input
                id="linea-nombre"
                value={values.nombre}
                onChange={handleChange('nombre')}
                className="h-10 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.94)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>

          <div className="col-span-12 md:col-span-2">
            <label className="flex h-full min-h-[68px] flex-col justify-between rounded-2xl border border-[rgba(var(--border),0.52)] bg-[rgba(var(--subpanel),0.72)] px-3 py-2">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.9)]">Estado</span>
              <span className="text-sm font-semibold text-[rgb(var(--fg))]">{values.activa ? 'Activa' : 'Inactiva'}</span>
              <span className="inline-flex items-center gap-2 text-sm text-[rgba(var(--fg),0.76)]">
                <input type="checkbox" checked={values.activa} onChange={handleChange('activa')} />
                Operativa
              </span>
            </label>
          </div>

          <div className="col-span-12">
            <Field
              label="Descripción / tramo operativo"
              htmlFor="linea-descripcion"
              description="Estos límites se usan para validar la emisión de fojas y la carga por línea."
            >
              <textarea
                id="linea-descripcion"
                value={values.descripcion}
                onChange={handleChange('descripcion')}
                rows={2}
                className="min-h-[72px] w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.94)] px-3 py-2 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgba(var(--border),0.58)] bg-[rgba(var(--panel),0.96)] p-3 shadow-[0_10px_26px_rgba(var(--shadow),0.08)] md:p-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Límites operativos</h2>
            <p className="text-[12px] text-[rgba(var(--muted),0.88)]">Carga máxima por línea para control de emisión.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-5">
          {LIMIT_FIELDS.map(([key, label, helper]) => (
            <LimitFieldCard
              key={key}
              label={label}
              helper={helper}
              value={values[key]}
              onChange={handleChange(key)}
            />
          ))}
        </div>
      </section>

      <StickyActions
        onCancel={onCancel}
        onSubmit={handleSubmit}
        primaryLabel={submitText}
      />
    </form>
  );
}
